import { ResizeEdge, type LiveProject, type ProjectsById, type Span } from '@wiztime/core';
import { memo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { BlockStatus } from '@wiztime/core';
import { cx } from '../../../lib/cx';
import { formatDuration, formatRange } from '../../../lib/format';
import { projectHex } from '../../../lib/projectColor';
import { OpenDayIcon } from '../../../ui/icons';
import type { AnchorRect } from '../dayUiStore';
import {
  blockItemId,
  isCompactBlock,
  isRoomyBlock,
  isTinyBlock,
  isTitledSingleLine,
  isTitledTall,
  percentOf,
  type ConflictLayout,
  type DisplayGroup,
} from './laneModel';
import styles from './Timeline.module.css';

/** Without a project list a project shows its own colour; a missing project gets the fallback colour (data dictionary §6). */
const NO_PROJECTS: ProjectsById = new Map();
/** Piled overlap blocks shift this much down and right per stack step (ui-redesign §3.1.4). */
export const STACK_OFFSET_DAY_PX = 4;
export const STACK_OFFSET_WEEK_PX = 3;

/** A learning tip (gesture hints on the second line) waits a little longer (ui-redesign §3.4.3). */
export const LEARNING_TIP_DELAY_MS = 250;

/** A block shorter than this shows no note line, whatever the lane width (ui-redesign §3.7). */
export const NOTE_LINE_MIN_BLOCK_PX = 44;

interface TimeBlockCardProps {
  readonly group: DisplayGroup;
  readonly project: LiveProject | undefined;
  /** Every project, to resolve a child project's inherited colour; omitted: the project's own colour. */
  readonly projectsById?: ProjectsById;
  readonly dayStartMin: number;
  readonly laneHeightPx: number;
  readonly isNow: boolean;
  readonly selected: boolean;
  readonly conflict: ConflictLayout | undefined;
  /** Confirmed block overlapping another confirmed block: it carries the overlap marker (§3.8). */
  readonly overlapMarked?: boolean;
  /** Stretches whose minutes count towards another block: drawn with the 135° hatch (§2.1.5). */
  readonly stolen?: readonly Span[];
  /** The block's overlap column is too narrow for text: colour bar only (§3.1.4). */
  readonly narrowColumn?: boolean;
  /**
   * First line of the block's note (day view only, ui-redesign §3.7): drawn under the text row
   * when the block is at least 44px tall and sits in no overlap column; the stylesheet shows
   * it only while the lane is at least 440px wide.
   */
  readonly note?: string;
  /** Pixel shift per pile step of blocks beyond the column cap: 4 in the day lane, 3 in a week lane. */
  readonly stackStepPx?: number;
  readonly dragging: boolean;
  readonly interactive: boolean;
  /** This block holds the lane's single tab stop (roving tabindex); the others are reached with the arrow keys. */
  readonly tabbable: boolean;
  /** The overlap popover is open for this block or a block it overlaps: 2px accent ring (§3.8). */
  readonly resolving: boolean;
  /** Id of the visible tooltip describing this block. */
  readonly describedBy?: string;
  /** Week view: route of the day view this block's date opens; shown as a small icon link. */
  readonly dayLinkTo?: string;
  /** Week lane (narrow column): titled blocks show the title alone, wrapping when roomy. */
  readonly weekLane?: boolean;
  /**
   * Whole-move drag (ui-redesign §2.4): vertical offset in pixels, applied as
   * `transform: translateY` with no transition; the geometry (top) stays at the original span.
   */
  readonly shiftPx?: number;
  /** Cross-day move in the week view (§2.8): horizontal offset onto the target lane, in pixels. */
  readonly shiftXPx?: number;
  /** Span the moved block will occupy on release: shown as the card's time text while dragging. */
  readonly shiftedSpan?: Span;
  /** A touch long press armed a move on this block (§3.3): lifted, slightly widened, ringed. */
  readonly armed?: boolean;
  /** Quick-mark mode (§3.2): hovering previews the block in the chosen project's colour. */
  readonly quickColor?: string;
  /** Native tooltip while quick-mark hovering: "click to change to …" (§3.2 QuickMark). */
  readonly quickTip?: string;
  /**
   * Learning-period gesture hints (ui-redesign §3.6.2): the second line of the hover tip
   * ("drag to move · drag the edges to adjust") until those gestures retire.
   */
  readonly learnHint?: string;
  readonly onOpen: (group: DisplayGroup, anchor: AnchorRect) => void;
  readonly onConflict: (group: DisplayGroup, anchor: AnchorRect) => void;
}

/**
 * One block (or merged group): colour-tinted card with name, draft and "now" badges, and edge handles.
 * Memoised: a drag re-renders the lane at every snapped step, and only the dragged card changes.
 */
export const TimeBlockCard = memo(function TimeBlockCard(props: TimeBlockCardProps) {
  const {
    group,
    project,
    projectsById = NO_PROJECTS,
    dayStartMin,
    laneHeightPx,
    isNow,
    selected,
    conflict,
    overlapMarked = false,
    stolen,
    narrowColumn = false,
    stackStepPx = STACK_OFFSET_DAY_PX,
    dragging,
    interactive,
    tabbable,
    resolving,
    shiftPx,
    shiftXPx,
    shiftedSpan,
    armed,
    quickColor,
  } = props;
  const { t } = useTranslation();
  // Quick-mark mode previews the chosen project's colour on hover (ui-redesign §3.2 QuickMark).
  const [quickHover, setQuickHover] = useState(false);
  const isDraft = group.status === BlockStatus.Draft;
  const name = project?.data.name ?? t('timeline.unknownProject');
  const title = group.title;
  const shown = shiftedSpan ?? { startMin: group.startMin, endMin: group.endMin };
  const range = formatRange(shown.startMin, shown.endMin);
  const duration = formatDuration(shown.endMin - shown.startMin);
  const compact = isCompactBlock(group.startMin, group.endMin, laneHeightPx);
  // Too short for one line of text: only the colour bar is drawn; the tooltip carries the information.
  const tiny = isTinyBlock(group.startMin, group.endMin, laneHeightPx);
  const roomy = isRoomyBlock(group.startMin, group.endMin, laneHeightPx);
  // Titled typography (2026-10b §5.2): under 32px one line (title + project), under 44px the
  // 13px title with a "project · range · duration" second line, from 44px the 14px title.
  const titledSingle = title !== null && isTitledSingleLine(group.startMin, group.endMin, laneHeightPx);
  const titledTall = title !== null && isTitledTall(group.startMin, group.endMin, laneHeightPx);
  const tipLine1 = [
    ...(title === null ? [] : [title]),
    name,
    range,
    duration,
    ...(isDraft ? [t('draft.badge')] : []),
    ...(overlapMarked ? [t('timeline.overlap')] : []),
  ].join(' · ');
  // Learning tips carry the gesture hints on a second line (§3.6.2) and wait 250ms instead of
  // the 120ms of a short block whose text is hidden (§3.4.3).
  const learnHint = props.learnHint;
  const tip = learnHint === undefined ? tipLine1 : `${tipLine1}\n${learnHint}`;
  const tipDelay = learnHint !== undefined && !tiny && !compact ? LEARNING_TIP_DELAY_MS : undefined;
  const top = percentOf(group.startMin, dayStartMin);
  const height = percentOf(group.endMin, dayStartMin) - top;
  // Note line (ui-redesign §3.7): at least 44px of block height and no overlap column; the
  // lane-width half of the condition (>= 440px) lives in the stylesheet's container query.
  const blockHeightPx = (height / 100) * laneHeightPx;
  const showNote =
    props.note !== undefined &&
    props.note !== '' &&
    conflict === undefined &&
    !tiny &&
    !compact &&
    blockHeightPx >= NOTE_LINE_MIN_BLOCK_PX;
  const columns = conflict?.columns ?? 1;
  const colSpan = conflict?.span ?? 1;
  // Blocks beyond the side-by-side columns are piled up, each shifted a few px down and right.
  const shift = (conflict?.stack ?? 0) * stackStepPx;

  // Blocks never fill the lane: the create band on the right stays free as a one-click overlay
  // entry (ui-redesign §3.1.3). `--create-band` is set per lane variant in Timeline.module.css.
  const available = `(100% - var(--create-band))`;
  const blockColor =
    quickColor !== undefined && quickHover && !dragging
      ? quickColor
      : projectHex(project, projectsById);
  const style = {
    top: `calc(${top}% + ${1 + shift}px)`,
    height: `max(6px, calc(${height}% - 2px))`,
    left: conflict === undefined ? 0 : `calc(${available} * ${conflict.column / columns} + ${1 + shift}px)`,
    width:
      conflict === undefined
        ? `calc(${available})`
        : `calc(${available} * ${colSpan / columns} - ${2 + shift}px)`,
    zIndex: conflict !== undefined && conflict.stack > 0 ? 2 + conflict.stack : undefined,
    '--block-color': blockColor,
    // Whole moves follow the pointer by translateY with no transition (ui-redesign §2.4);
    // a cross-day move in the week view adds the horizontal offset onto the target lane (§2.8).
    '--drag-shift': `${shiftPx ?? 0}px`,
    '--drag-shift-x': `${shiftXPx ?? 0}px`,
  } as CSSProperties;

  const open = (element: HTMLElement): void => {
    const rect = element.getBoundingClientRect();
    props.onOpen(group, { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    open(event.currentTarget);
  };

  return (
    <div
      className={cx(
        styles.block,
        isDraft && styles.draft,
        isNow && styles.current,
        selected && styles.selected,
        compact && styles.compact,
        tiny && styles.tiny,
        narrowColumn && !tiny && styles.narrow,
        roomy && styles.roomy,
        resolving && styles.resolving,
        dragging && styles.dragging,
        shiftPx !== undefined && styles.shifted,
        armed === true && styles.armed,
        quickColor !== undefined && quickHover && styles.quickTarget,
        interactive && styles.movable,
        conflict !== undefined && styles.conflicting,
      )}
      style={style}
      role="button"
      tabIndex={interactive && tabbable ? 0 : -1}
      aria-label={
        (title === null
          ? t(isDraft ? 'timeline.blockLabelDraft' : 'timeline.blockLabel', { project: name, range })
          : t('timeline.blockLabelTitled', { title, project: name, range }) +
            (isDraft ? `, ${t('draft.badge')}` : '')) +
        (overlapMarked ? `, ${t('timeline.blockLabelOverlap')}` : '')
      }
      aria-pressed={selected}
      aria-haspopup="menu"
      aria-describedby={props.describedBy}
      data-tip={tip}
      data-tip-delay={tipDelay}
      data-lane-item={blockItemId(group.key)}
      data-group-key={group.key}
      data-testid="time-block"
      data-block-id={group.key}
      data-status={group.status}
      data-start={shown.startMin}
      data-end={shown.endMin}
      data-members={group.members.length}
      data-tiny={tiny}
      data-resolving={resolving ? '' : undefined}
      onKeyDown={onKeyDown}
      onPointerEnter={quickColor === undefined ? undefined : () => setQuickHover(true)}
      onPointerLeave={quickColor === undefined ? undefined : () => setQuickHover(false)}
      title={quickColor !== undefined && quickHover ? props.quickTip : undefined}
    >
      <span className={styles.handleStart} data-edge={ResizeEdge.Start} data-testid="handle-start" />
      {/* Stretches counted towards another block: 135° hatch, never on drafts (§2.1.5). */}
      {!isDraft && stolen !== undefined
        ? stolen.map((piece) => (
            <span
              key={piece.startMin}
              className={styles.hatch}
              style={{
                top: `${((piece.startMin - group.startMin) / (group.endMin - group.startMin)) * 100}%`,
                height: `${((piece.endMin - piece.startMin) / (group.endMin - group.startMin)) * 100}%`,
              }}
              aria-hidden="true"
              data-testid="overlap-hatch"
              data-start={piece.startMin}
              data-end={piece.endMin}
            />
          ))
        : null}
      {tiny || narrowColumn ? null : title === null ? (
        <div className={styles.blockBody}>
          <span className={styles.name}>{name}</span>
          {isDraft ? <span className={styles.badge}>{t('draft.badge')}</span> : null}
          {isNow ? <span className={cx(styles.badge, styles.nowBadge)}>{t('draft.now')}</span> : null}
          {!compact ? (
            <span className={styles.meta}>
              {range} · {duration}
            </span>
          ) : null}
        </div>
      ) : props.weekLane === true ? (
        // Week lane (2026-10b §5.2): the title is the block's text; the project is carried by
        // the colour and the tooltip. A roomy block may wrap the title onto two lines.
        <div className={styles.blockBody}>
          <span className={cx(styles.name, styles.blockTitle)} data-testid="block-title">
            {title}
          </span>
          {isDraft ? <span className={styles.badge}>{t('draft.badge')}</span> : null}
        </div>
      ) : titledSingle ? (
        // 18-32px (§5.2): one line, title first (12px/600), then the project name; the time is
        // omitted, and the project name gives way before the title does.
        <div className={cx(styles.blockBody, styles.titledRow)}>
          <span className={cx(styles.blockTitle, styles.titledSmall)} data-testid="block-title">
            {title}
          </span>
          <span className={styles.titledName}>{name}</span>
          {isDraft ? <span className={styles.badge}>{t('draft.badge')}</span> : null}
        </div>
      ) : (
        // From 32px (§5.2): the title line, then "project · range · duration" (12px/500); the
        // duration gives way first, then the range, then the project name.
        <>
          <div className={styles.blockBody}>
            <span
              className={cx(styles.blockTitle, titledTall && styles.titledTallTitle)}
              data-testid="block-title"
            >
              {title}
            </span>
            {isDraft ? <span className={styles.badge}>{t('draft.badge')}</span> : null}
            {isNow ? <span className={cx(styles.badge, styles.nowBadge)}>{t('draft.now')}</span> : null}
          </div>
          <div className={styles.titledMeta}>
            <span className={styles.titledMetaName}>{name}</span>
            <span className={styles.titledMetaRange}>&nbsp;· {range}</span>
            <span className={styles.titledMetaDuration}>&nbsp;· {duration}</span>
          </div>
        </>
      )}
      {showNote ? (
        <span className={styles.note} data-testid="block-note">
          {props.note}
        </span>
      ) : null}
      {overlapMarked ? (
        <button
          type="button"
          className={cx(styles.overlapBadge, (tiny || narrowColumn) && styles.overlapBar)}
          data-ignore-lane
          data-testid="overlap-badge"
          aria-label={t('timeline.overlapLabel')}
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            props.onConflict(group, {
              left: rect.left,
              top: rect.top,
              right: rect.right,
              bottom: rect.bottom,
            });
          }}
        >
          {/* Two overlapping rectangles, 16px (§3.8); the hit area is the 24px button. */}
          <svg className={styles.overlapIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" />
            <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
          </svg>
        </button>
      ) : null}
      {props.dayLinkTo !== undefined && !tiny ? (
        <Link
          to={props.dayLinkTo}
          className={styles.openDay}
          data-ignore-lane
          data-testid="open-day"
          aria-label={t('week.openDayOfBlock', { project: name, range })}
          title={t('week.openDay')}
        >
          <OpenDayIcon />
        </Link>
      ) : null}
      <span className={styles.handleEnd} data-edge={ResizeEdge.End} data-testid="handle-end" />
    </div>
  );
});
